import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ConfigProvider, theme, Button } from "antd";
import {
  EnvironmentOutlined,
  LoadingOutlined,
  LockOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TenderProvider } from "./contexts/TenderContext";
import { AutoSaveProvider } from "./contexts/AutoSaveContext";
import { ChangeDetectionProvider } from "./contexts/ChangeDetectionContext";
import { DevModeProvider } from "./contexts/DevModeContext";
import { ConfigureBoqProvider } from "./contexts/ConfigureBoqContext";
import TenderInfo from "./components/forms/TenderInfo";
import JobTypeSelection from "./components/forms/JobTypeSelection";
import TenderJobTypes from "./components/forms/TenderJobTypes";
import TenderDetails from "./components/forms/TenderDetails";
import TenderImportTakeoff from "./components/forms/TenderImportTakeoff";
import WorkflowContainer from "./components/layout/WorkflowContainer";
import FoulSewersTakeoff from "./components/forms/FoulSewersTakeoff";
import StormSewersTakeoff from "./components/forms/StormSewersTakeoff";
import RoadsTakeoff from "./components/forms/RoadsTakeoff";
import MainServices from "./components/forms/MainServices";
import PreliminaryItems from "./components/forms/PreliminaryItems";
import ReviewCalculate from "./components/forms/ReviewCalculate";
import POS from "./components/forms/POS";
import SpoilReconciliation from "./components/forms/SpoilReconciliation";
import Compound from "./components/forms/Compound";
import BillOfQuantities from "./components/forms/BillOfQuantities";
import PlotMeasure from "./components/forms/groundworks/PlotMeasure";
import FoundationsNew from "./components/forms/groundworks/FoundationsNew";
import Drainage from "./components/forms/groundworks/Drainage";
import PlotDrives from "./components/forms/groundworks/PlotDrives";
import PlotService from "./components/forms/groundworks/PlotService";
import PlotPavings from "./components/forms/groundworks/PlotPavings";
import Scaffold from "./components/forms/groundworks/Scaffold";
import Garden from "./components/forms/groundworks/Garden";
import RetainingWalls from "./components/forms/groundworks/RetainingWalls";
import CutFills from "./components/forms/groundworks/CutFills";
import GeoToberWall from "./components/forms/groundworks/GeoToberWall";
import PlotMatrix from "./components/forms/groundworks/PlotMatrix";
import GroundworksReview from "./components/forms/groundworks/GroundworksReview";
import ErrorBoundary from "./components/common/ErrorBoundary";
import NotFound from "./components/common/NotFound";
import { ToastProvider } from "./components/common/VisualFeedback";
import { PageLoading } from "./components/common/LoadingStates";
import DataRecovery from "./utils/dataRecovery";
import {
  useKeyboardShortcuts,
  createGlobalShortcuts,
} from "./hooks/useKeyboardShortcuts";
import OpportunitiesDashboard from "./components/pages/OpportunitiesDashboard";
import VersionHistory from "./components/pages/VersionHistory";
import AuditTrail from "./components/pages/AuditTrailPage";
import NewTender from "./pages/tender";
import JobSelection from "./pages/job-selection";
import { DocumentLibrary } from "./pages/document-library";
import TakeoffConfiguration from "./pages/takeoff-configuration";
import AuthGuard from "./components/auth/AuthGuard";
import { useAuthSync } from "./hooks/useAuthSync";
import RateEngine from "./features/rate-engine";
import { UserProvider, useUser } from "./contexts/UserContext";
import { logOut } from "./hooks";

// Ant Design theme configuration
const antdTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: "#003366",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    colorInfo: "#1890ff",
    borderRadius: 6,
    fontFamily:
      '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
    // Sidebar is z-index 1001, header is 1002 — raise all popups above both
    zIndexPopupBase: 1100,
  },
  components: {
    Button: {
      fontWeight: 500,
      borderRadius: 6,
      defaultShadow: "none",
      primaryShadow: "none",
    },
    Input: {
      borderRadius: 6,
    },
    Select: {
      borderRadius: 6,
    },
    Card: {
      borderRadius: 8,
    },
    Table: {
      borderRadius: 8,
    },
  },
};

const DashboardPage: React.FC = () => <OpportunitiesDashboard />;
const NotFoundPage: React.FC = () => <NotFound />;
const VersionHistoryPage: React.FC = () => <VersionHistory />;
const AuditTrailPage: React.FC = () => <AuditTrail />;
const DocumentLibraryPage: React.FC = () => <DocumentLibrary />;
const TakeoffConfigurationPage: React.FC = () => <TakeoffConfiguration />;
const TenderInfoPage: React.FC = () => <TenderInfo />;
const NewTenderPage: React.FC = () => <NewTender />;
const JobTypeSelectionPage: React.FC = () => <JobTypeSelection />;
const JobSelectionPage: React.FC = () => <JobSelection />;
const TenderJobTypesPage: React.FC = () => <TenderJobTypes />;
const TenderDetailsPage: React.FC = () => <TenderDetails />;
const TenderImportTakeoffPage: React.FC = () => <TenderImportTakeoff />;

// Roads & Sewers Pages
const FoulSewersPage: React.FC = () => <FoulSewersTakeoff />;
const StormSewersPage: React.FC = () => <StormSewersTakeoff />;
const RoadsPage: React.FC = () => <RoadsTakeoff />;
const MainServicesPage: React.FC = () => <MainServices />;
const PreliminaryPage: React.FC = () => <PreliminaryItems />;
const ReviewPage: React.FC = () => <ReviewCalculate />;
const POSPage: React.FC = () => <POS />;
const SpoilReconciliationPage: React.FC = () => <SpoilReconciliation />;
const CompoundPage: React.FC = () => <Compound />;

// Groundworks Pages
const PlotMeasurePage: React.FC = () => <PlotMeasure />;
const FoundationsPage: React.FC = () => <FoundationsNew />;
const DrainagePage: React.FC = () => <Drainage />;
const PlotDrivesPage: React.FC = () => <PlotDrives />;
const PlotServicePage: React.FC = () => <PlotService />;
const PlotPavingsPage: React.FC = () => <PlotPavings />;
const ScaffoldPage: React.FC = () => <Scaffold />;
const GardenPage: React.FC = () => <Garden />;
const RetainingWallsPage: React.FC = () => <RetainingWalls />;
const CutFillsPage: React.FC = () => <CutFills />;
const GeoToberWallPage: React.FC = () => <GeoToberWall />;
const PlotMatrixPage: React.FC = () => <PlotMatrix />;
const GroundworksReviewPage: React.FC = () => <GroundworksReview />;

// Shared Pages
const BillQuantitiesPage: React.FC = () => <BillOfQuantities />;

// Rate Engine
const RateEnginePage: React.FC = () => <RateEngine />;

// Create a query client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

/**
 * Runs the cross-app auth sync check once authentication has resolved, so a
 * logout or an account switch in MJ-Extended-PM or MJ-Tracker doesn't leave
 * this app running as the previous account. Renders nothing. See
 * utils/authSync.
 */
function AuthSync() {
  useAuthSync();
  return null;
}

function App() {
  // Crash recovery detection
  useEffect(() => {
    const recoveredData = DataRecovery.detectCrashRecovery();
    if (recoveredData) {
      // Data will be handled by the TenderProvider
      // The data will be handled by the TenderProvider
    }
  }, []);


  // Global keyboard shortcuts
  const shortcuts = createGlobalShortcuts(
    () => {}, // TODO: Connect to actual save function
    () => {}, // TODO: Add new row functionality
    () => {}, // TODO: Remove selected row functionality
    () => {}, // TODO: Next field navigation
    () => {}, // TODO: Previous field navigation
    () => {}, // TODO: Show help functionality
  );

  useKeyboardShortcuts({
    shortcuts,
    enabled: true,
  });

  return (
    <>
      <ErrorBoundary
        showErrorDetails={
          import.meta.env.MODE === "development" ||
          import.meta.env.MODE === "staging"
        }
      >
        <DevModeProvider>
        <ConfigureBoqProvider>
          <QueryClientProvider client={queryClient}>
            <ConfigProvider theme={antdTheme}>
              <AuthGuard>
                <AuthSync />
                <div className="min-h-screen bg-gray-50">
                  <ToastProvider>
                    <TenderProvider>
                      <ChangeDetectionProvider>
                        <AutoSaveProvider>
                          <UserProvider>
                            <AppRouter />
                          </UserProvider>
                        </AutoSaveProvider>
                      </ChangeDetectionProvider>
                    </TenderProvider>
                  </ToastProvider>
                </div>
              </AuthGuard>
            </ConfigProvider>
          </QueryClientProvider>
        </ConfigureBoqProvider>
        </DevModeProvider>
      </ErrorBoundary>
    </>
  );
}

function AppRouter() {
  const user = useUser();

  if (user?.loading) {
    return (
      <div className="flex h-full items-center justify-center gap-x-1.5 p-[100px]">
        <LoadingOutlined />
        <span>Loading...</span>
      </div>
    );
  }

  if (user?.fetchSuccess) {
    if (user.hasAccess) {
      return (
        <Router>
          <Suspense fallback={<PageLoading message="Loading application..." />}>
            <Routes>
              {/* Dashboard - Landing Page */}
              <Route path="/" element={<DashboardPage />} />
              <Route path="/opportunities" element={<DashboardPage />} />
              <Route
                path="/version-history/:projectName"
                element={<VersionHistoryPage />}
              />
              <Route path="/audit-trail" element={<AuditTrailPage />} />
              <Route
                path="/document-library"
                element={<DocumentLibraryPage />}
              />
              <Route
                path="/takeoff-configuration"
                element={<TakeoffConfigurationPage />}
              />

              {/* Initial Setup */}
              {/* <Route path="/tender-info" element={<TenderInfoPage />} /> */}
              {/* <Route path="/new-tender" element={<NewTenderPage />} /> */}
              {/* <Route path="/job-types" element={<JobTypeSelectionPage />} /> */}
              {/* <Route path="/job-selection" element={<JobSelectionPage />} /> */}
              <Route
                path="/tender/job-types"
                element={<TenderJobTypesPage />}
              />
              <Route path="/tender/import-opportunities" element={<TenderDetailsPage />} />
              <Route path="/tender/import-takeoff" element={<TenderImportTakeoffPage />} />

              {/* Workflow Container with nested routes */}
              <Route path="/" element={<WorkflowContainer />}>
                {/* Roads & Sewers Workflow */}
                <Route path="roads-sewers">
                  <Route path="foul-sewers" element={<FoulSewersPage />} />
                  <Route path="storm-sewers" element={<StormSewersPage />} />
                  <Route path="roads" element={<RoadsPage />} />
                  <Route path="main-services-substation" element={<MainServicesPage />} />
                  <Route path="preliminary" element={<PreliminaryPage />} />
                  <Route path="review" element={<ReviewPage />} />
                  <Route path="pos" element={<POSPage />} />
                  <Route path="spoil-reconciliation" element={<SpoilReconciliationPage />} />
                  <Route path="compound" element={<CompoundPage />} />
                </Route>

                {/* Groundworks Workflow */}
                <Route path="groundworks">
                  <Route path="cut-fills" element={<CutFillsPage />} />
                  {/* <Route
                              path="plot-measure"
                              element={<PlotMeasurePage />}
                            /> */}
                  <Route path="foundations" element={<FoundationsPage />} />
                  <Route path="drainage" element={<DrainagePage />} />
                  <Route path="plot-drives" element={<PlotDrivesPage />} />
                  <Route path="plot-service" element={<PlotServicePage />} />
                  <Route path="plot-pavings" element={<PlotPavingsPage />} />
                  <Route path="scaffold" element={<ScaffoldPage />} />
                  <Route path="garden" element={<GardenPage />} />
<Route
                    path="retaining-walls"
                    element={<RetainingWallsPage />}
                  />
                  <Route path="geo-tober-wall" element={<GeoToberWallPage />} />
                  <Route path="plot-matrix" element={<PlotMatrixPage />} />
                  <Route path="review" element={<GroundworksReviewPage />} />
                </Route>
              </Route>

              {/* Shared Final Step */}
              <Route path="/bill-quantities" element={<BillQuantitiesPage />} />

              {/* Rate Engine */}
              <Route path="/rate-engine/*" element={<RateEnginePage />} />

              {/* Legacy routes for backward compatibility */}
              <Route
                path="/foul-sewers"
                element={<Navigate to="/roads-sewers/foul-sewers" replace />}
              />
              <Route
                path="/storm-sewers"
                element={<Navigate to="/roads-sewers/storm-sewers" replace />}
              />
              <Route
                path="/roads"
                element={<Navigate to="/roads-sewers/roads" replace />}
              />
              <Route
                path="/main-services"
                element={<Navigate to="/roads-sewers/main-services-substation" replace />}
              />
              <Route
                path="/preliminary"
                element={<Navigate to="/roads-sewers/preliminary" replace />}
              />
              <Route
                path="/review"
                element={<Navigate to="/roads-sewers/review" replace />}
              />

              {/* Fallback — any unmatched URL gets a proper 404 instead of a
                  silent redirect, with a way back to the dashboard. */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Router>
      );
    } else if (!!user.roleDisplayName?.trim() && !user.hasRegion) {
      return (
        <div className="flex h-full min-h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md w-full mx-4 bg-white rounded-xl shadow-lg p-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mx-auto mb-6">
              <EnvironmentOutlined style={{ fontSize: 28, color: "#3b82f6" }} />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-3">
              Almost There
            </h1>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Your account is set up, but it still needs a region assigned
              before you can access this module.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Ask your system administrator to add a region to your account -
              it only takes a moment.
            </p>
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={logOut}
              size="large"
            >
              Logout
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex h-full min-h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md w-full mx-4 bg-white rounded-xl shadow-lg p-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-6">
              <LockOutlined style={{ fontSize: 28, color: "#dc2626" }} />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-3">
              Access Denied
            </h1>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Your account has not been configured as part of the Project Team
              and therefore you do not have the required permissions to access
              this module.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Please contact your system administrator to have your account set
              up with the appropriate permissions.
            </p>
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={logOut}
              size="large"
            >
              Logout
            </Button>
          </div>
        </div>
      );
    }
  }

  return null;
}

// Exported (in addition to the default App) so AppRouter's access-gate/banner
// logic can be unit-tested without mounting AuthGuard/MSAL.
export { AppRouter };
export default App;
